import React from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import axios from "axios";
import { API_send_support_feedback } from "../../../../constants";
import { getDefaultAxiosConfig } from "../../../../utilities/requests";
import useSupportFeedbackStore from "../../../../stores/supportFeedbackStore";

const CONTEXT_TITLES = {
  "headcount-planning": "Headcount Planning Beta",
};

function SupportFeedbackModal({ homeStore }) {
  const [supportMessage, setSupportMessage] = React.useState("");
  const [supportLoading, setSupportLoading] = React.useState(false);
  const [supportSubmitted, setSupportSubmitted] = React.useState(false);
  const { isSupportModalOpen, closeSupportModal, modalContext } =
    useSupportFeedbackStore();

  const handleClose = React.useCallback(() => {
    closeSupportModal();
    setSupportMessage("");
    setSupportLoading(false);
    setSupportSubmitted(false);
  }, [closeSupportModal]);

  React.useEffect(() => {
    if (!isSupportModalOpen) {
      setSupportMessage("");
      setSupportLoading(false);
      setSupportSubmitted(false);
    }
  }, [isSupportModalOpen]);

  const handleSupportSubmit = async () => {
    if (!supportMessage?.trim()) {
      return;
    }

    try {
      setSupportLoading(true);
      const axiosConfig = getDefaultAxiosConfig();
      await axios.post(
        API_send_support_feedback,
        { message: supportMessage?.trim(), context: modalContext, store_id: homeStore },
        axiosConfig
      );
      setSupportLoading(false);
      setSupportSubmitted(true);
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (error) {
      setSupportLoading(false);
      alert(
        "Failed to send support request. Please try again or contact us directly at the phone numbers provided."
      );
      console.error("Error sending support feedback:", error);
    }
  };

  const contextLabel = modalContext ? CONTEXT_TITLES[modalContext] : null;

  return (
    <Modal
      open={isSupportModalOpen}
      onClose={handleClose}
      aria-labelledby="support-modal-title"
      aria-describedby="support-modal-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 450,
          bgcolor: "background.paper",
          border: "2px solid #E2E8F0",
          borderRadius: "15px",
          boxShadow: 24,
          p: 4,
        }}
      >
        <div className="absolute right-2 top-2">
          <IconButton
            className="text-gray-400 hover:text-gray-600"
            onClick={handleClose}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        {supportSubmitted ? (
          <div className="text-center py-8">
            <div className="mb-4 flex justify-center">
              <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center">
                <svg
                  className="h-8 w-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Message Sent!
            </h3>
            <p className="text-sm text-gray-600">We'll get back to you soon.</p>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <div className="flex items-center space-x-3">
                <SupportAgentIcon className="text-violet-600 h-8 w-8" />
                <div>
                  <h3
                    id="support-modal-title"
                    className="text-xl font-semibold text-gray-900"
                  >
                    Support / Feedback
                  </h3>
                  {contextLabel && (
                    <p className="text-xs uppercase tracking-wide text-violet-600 font-semibold mt-1">
                      {contextLabel}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="mb-4">
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder="Please share any issues or feedback you have!"
                value={supportMessage}
                onChange={(e) => setSupportMessage(e.target.value)}
                disabled={supportLoading}
                InputProps={{
                  sx: {
                    backgroundColor: "white",
                    borderRadius: "10px",
                    padding: "10px",
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#6d28d9",
                    },
                  },
                }}
              />
            </div>

            <div className="flex items-center space-x-3">
              <button
                className="flex-1 px-4 py-2.5 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors duration-200 text-sm font-medium"
                onClick={handleClose}
                disabled={supportLoading}
              >
                Cancel
              </button>
              <button
                className="flex-1 px-4 py-2.5 rounded-lg bg-violet-600 text-white hover:bg-violet-700 transition-colors duration-200 text-sm font-medium flex items-center justify-center"
                onClick={handleSupportSubmit}
                disabled={supportLoading}
              >
                {supportLoading ? (
                  <CircularProgress size={20} className="text-white" />
                ) : (
                  "Send"
                )}
              </button>
            </div>
          </>
        )}
      </Box>
    </Modal>
  );
}

export default SupportFeedbackModal;
